import { addHours, differenceInDays } from 'date-fns';
import type { CSSProperties } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';
import { generatePath } from 'react-router';
import styled from 'styled-components';

import { Cards } from '@sorare/core/src/assets/icons/Cards';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Refresh } from '@sorare/core/src/atoms/icons/Refresh';
import { ScoreTarget } from '@sorare/core/src/atoms/icons/ScoreTarget';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import { Wheel } from '@sorare/core/src/atoms/icons/Wheel';
import { iconClock } from '@sorare/core/src/atoms/icons/system/iconClock';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyS,
  BrandT,
  BrandXS,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { BLOG_SPORT_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { sportToBlogSportMap } from '@sorare/core/src/lib/routing';
import {
  useCommonGameSet,
  useCurrentSetBackground,
  useSetLogoImage,
} from '@sorare/core/src/set';

import { TimeLeftBox } from './TimeLeftBox';

const StyledDialog = styled(Dialog)`
  background: linear-gradient(
      to bottom,
      transparent 0%,
      var(--c-black) calc(40 * var(--unit))
    ),
    var(--background);
`;
const Content = styled(Vertical).attrs({ gap: 3, center: true })`
  padding: 0 var(--triple-unit) var(--double-unit);
`;
const MainTextBlock = styled(Vertical).attrs({ gap: 2 })`
  align-items: flex-start;
  align-self: flex-start;
`;
const ItemWrapper = styled(Horizontal).attrs({ gap: 0.5 })`
  align-items: flex-start;
  color: var(--c-nd-600);
`;

const items = ({ setName, endDate }: { setName: string; endDate: Date }) => [
  {
    key: '1',
    message: (
      <FormattedMessage
        defaultMessage="{setName} Tickets expire — use them before they're gone"
        values={{ setName }}
      />
    ),
    icon: <Wheel size={2.5} />,
  },
  {
    key: '2',
    message: (
      <FormattedMessage
        defaultMessage="{setName} Essence converts to Legacy Essence"
        values={{ setName }}
      />
    ),
    icon: <Shards size={2} />,
  },
  {
    key: '3',
    message: (
      <FormattedMessage
        defaultMessage="{setName} cards converts to Legacy gameplay"
        values={{ setName }}
      />
    ),
    icon: <Cards size={2} />,
  },
  {
    key: '4',
    message: (
      <FormattedMessage
        defaultMessage="{setName} Streaks are still eligible through your last game {dayOfTheWeek}"
        values={{
          setName,
          dayOfTheWeek: <FormattedDate value={endDate} weekday="long" />,
        }}
      />
    ),
    icon: <ScoreTarget />,
  },
  {
    key: '5',
    message: (
      <FormattedMessage
        defaultMessage="Streaks reset {dayOfTheWeek} when the new set begins"
        values={{
          dayOfTheWeek: (
            <FormattedDate value={addHours(endDate, 14)} weekday="long" />
          ),
        }}
      />
    ),
    icon: <Refresh width={16} height={16} />,
  },
];

type Props = { open: boolean; onClose: () => void; endDate: Date };
export const SetWillEndDialog = ({ open, onClose, endDate }: Props) => {
  const { sport } = useSportContext();

  const { background } = useCurrentSetBackground();
  const commonGameSet = useCommonGameSet();
  const { displayName } = commonGameSet.cardSet;

  const lessThan1Day = differenceInDays(endDate, new Date()) <= 1;

  const color = lessThan1Day ? 'var(--c-red-400)' : 'var(--c-yellow-400)';
  const daysLeft = differenceInDays(endDate, new Date());
  const logoImage = useSetLogoImage()(undefined);

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      style={{ '--background': background } as CSSProperties}
      fullWidth
      maxWidth="xs"
    >
      <Content>
        <Vertical gap={2} center>
          <img src={logoImage} alt="" height={48} />
          <TimeLeftBox style={{ '--shadow-color': color } as CSSProperties}>
            <Horizontal gap={0.5} center>
              <FontAwesomeIcon color={color} icon={iconClock} size="xs" />
              <BrandXS color={color}>
                {lessThan1Day ? (
                  <TimeLeft time={endDate} />
                ) : (
                  <FormattedMessage
                    defaultMessage="{nbDays, plural, one {# day left} other {# days left}}"
                    values={{ nbDays: daysLeft }}
                  />
                )}
              </BrandXS>
            </Horizontal>
          </TimeLeftBox>
        </Vertical>
        <MainTextBlock>
          <Vertical gap={0.5}>
            <BrandT>
              {lessThan1Day ? (
                <FormattedMessage
                  defaultMessage="{setName} set Ends in less than <bold>24 hours.</bold>"
                  values={{
                    bold: chunks => (
                      <BrandT color={color} as="span" bold>
                        {chunks}
                      </BrandT>
                    ),
                    setName: displayName,
                  }}
                />
              ) : (
                <FormattedMessage
                  defaultMessage="{setName} set Ends this {day}."
                  values={{
                    day: <FormattedDate value={endDate} weekday="long" />,
                    setName: displayName,
                  }}
                />
              )}
            </BrandT>
            <BodyS>
              <FormattedMessage defaultMessage="Here's what to know to make the most of your last week." />
            </BodyS>
          </Vertical>
          <Vertical>
            <LabelM bold>
              <FormattedMessage
                defaultMessage="On {day} at {time}:"
                values={{
                  day: <FormattedDate value={endDate} weekday="long" />,
                  time: (
                    <FormattedTime
                      value={endDate}
                      hour="2-digit"
                      minute="2-digit"
                    />
                  ),
                }}
              />
            </LabelM>
            {items({ setName: displayName, endDate }).map(
              ({ message, key, icon }) => (
                <ItemWrapper key={key}>
                  {icon}
                  <LabelM color="var(--c-nd-600)">{message}</LabelM>
                </ItemWrapper>
              )
            )}
          </Vertical>
        </MainTextBlock>
        <Button
          size="large"
          color="quaternary"
          to={generatePath(BLOG_SPORT_SLUG, {
            sport: sportToBlogSportMap[sport],
            slug: 'wntr-set-end',
          })}
          externalLink
        >
          <FormattedMessage defaultMessage="Learn more" />
        </Button>
      </Content>
    </StyledDialog>
  );
};
